import { createContext, useContext, useState, useEffect } from 'react';

const ThemeCtx = createContext({ isDark: true, toggleTheme: () => {} });

const DARK = {
  '--rn-bg':                '#08011a',
  '--rn-nav-bg':            'rgba(8,1,26,0.85)',
  '--rn-nav-border':        'rgba(255,255,255,0.07)',
  '--rn-text':              '#ffffff',
  '--rn-text-sub':          'rgba(255,255,255,0.6)',
  '--rn-text-muted':        'rgba(255,255,255,0.45)',
  '--rn-text-faint':        'rgba(255,255,255,0.3)',
  '--rn-card-bg':           'rgba(255,255,255,0.04)',
  '--rn-card-border':       'rgba(255,255,255,0.08)',
  '--rn-card-border-lg':    'rgba(255,255,255,0.1)',
  '--rn-section-alt':       'rgba(255,255,255,0.025)',
  '--rn-section-border':    'rgba(255,255,255,0.06)',
  '--rn-ghost-border':      'rgba(255,255,255,0.25)',
  '--rn-ghost-color':       '#ffffff',
  '--rn-nav-btn-border':    'rgba(255,255,255,0.2)',
  '--rn-nav-btn-color':     '#ffffff',
  '--rn-muted-btn-color':   'rgba(255,255,255,0.5)',
  '--rn-orb1':              'rgba(37,99,235,0.55)',
  '--rn-orb2':              'rgba(6,182,212,0.30)',
  '--rn-orb3':              'rgba(217,70,239,0.22)',
  '--rn-emp-orb2':          'rgba(16,185,129,0.30)',
  '--rn-callout-title':     '#fde68a',
  '--rn-portal-bg':         '#0a0f1e',
  '--rn-portal-surface':    'rgba(255,255,255,0.06)',
  '--rn-portal-topbar':     'rgba(10,15,30,0.96)',
  '--rn-portal-border':     'rgba(255,255,255,0.10)',
  '--rn-portal-text':       '#ffffff',
  '--rn-portal-text-sub':   'rgba(255,255,255,0.60)',
  '--rn-portal-text-muted': 'rgba(255,255,255,0.38)',
  '--rn-portal-input-bg':   'rgba(255,255,255,0.07)',
  '--rn-portal-input-border':'rgba(255,255,255,0.15)',
  '--rn-portal-input-color':'#e2e8f0',
  '--rn-form-bg':           '#0a1628',
  '--rn-form-text':         '#f0f6ff',
  '--rn-form-sub':          'rgba(255,255,255,0.55)',
  '--rn-input-bg':          'rgba(255,255,255,0.07)',
  '--rn-input-border':      'rgba(255,255,255,0.14)',
  '--rn-input-color':       '#e2e8f0',
  '--rn-outline-btn-bg':    'rgba(255,255,255,0.06)',
  '--rn-outline-btn-border':'rgba(255,255,255,0.18)',
  '--rn-outline-btn-color': 'rgba(255,255,255,0.85)',
  '--rn-divider':           'rgba(255,255,255,0.1)',
};

const LIGHT = {
  '--rn-bg':                '#f0f6ff',
  '--rn-nav-bg':            'rgba(240,246,255,0.92)',
  '--rn-nav-border':        'rgba(0,0,0,0.08)',
  '--rn-text':              '#0f172a',
  '--rn-text-sub':          'rgba(15,23,42,0.65)',
  '--rn-text-muted':        'rgba(15,23,42,0.45)',
  '--rn-text-faint':        'rgba(15,23,42,0.3)',
  '--rn-card-bg':           'rgba(0,0,0,0.03)',
  '--rn-card-border':       'rgba(0,0,0,0.08)',
  '--rn-card-border-lg':    'rgba(0,0,0,0.1)',
  '--rn-section-alt':       'rgba(0,0,0,0.025)',
  '--rn-section-border':    'rgba(0,0,0,0.06)',
  '--rn-ghost-border':      'rgba(0,0,0,0.2)',
  '--rn-ghost-color':       '#0f172a',
  '--rn-nav-btn-border':    'rgba(0,0,0,0.18)',
  '--rn-nav-btn-color':     '#0f172a',
  '--rn-muted-btn-color':   'rgba(15,23,42,0.5)',
  '--rn-orb1':              'rgba(37,99,235,0.18)',
  '--rn-orb2':              'rgba(6,182,212,0.13)',
  '--rn-orb3':              'rgba(217,70,239,0.11)',
  '--rn-emp-orb2':          'rgba(16,185,129,0.12)',
  '--rn-callout-title':     '#92400e',
  '--rn-portal-bg':         '#f0f7ff',
  '--rn-portal-surface':    '#ffffff',
  '--rn-portal-topbar':     '#ffffff',
  '--rn-portal-border':     '#e5e7eb',
  '--rn-portal-text':       '#111827',
  '--rn-portal-text-sub':   '#6b7280',
  '--rn-portal-text-muted': '#9ca3af',
  '--rn-portal-input-bg':   '#ffffff',
  '--rn-portal-input-border':'#d1d5db',
  '--rn-portal-input-color':'#0f172a',
  '--rn-form-bg':           '#dbeafe',
  '--rn-form-text':         '#0f172a',
  '--rn-form-sub':          '#3b5a8a',
  '--rn-input-bg':          '#f0f7ff',
  '--rn-input-border':      '#93c5fd',
  '--rn-input-color':       '#0f172a',
  '--rn-outline-btn-bg':    '#eff6ff',
  '--rn-outline-btn-border':'#93c5fd',
  '--rn-outline-btn-color': '#1e40af',
  '--rn-divider':           '#93c5fd',
};

function applyVars(isDark) {
  const vars = isDark ? DARK : LIGHT;
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
}

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem('rn_theme');
      const dark = saved !== 'light';
      applyVars(dark);
      return dark;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    applyVars(isDark);
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(prev => {
      const next = !prev;
      try { localStorage.setItem('rn_theme', next ? 'dark' : 'light'); } catch {}
      return next;
    });
  };

  return (
    <ThemeCtx.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeCtx);
}
